/**
 * deepspace skills <subcommand>
 *
 * Wraps `npx skills@latest add deepdotspace/deepspace-skill` so installation
 * lives under our verb (mirrors `npx remotion skills add`). The upstream
 * `skills` CLI owns repo fetch, idempotency, and agent layouts.
 *
 *   deepspace skills add               # multi-agent install
 *   deepspace skills add -g            # install globally
 *   deepspace skills add --agent codex # single agent
 *   deepspace skills add -y            # non-interactive
 *
 * Pre-install fixup for vercel-labs/skills#1138: upstream's project-local
 * install silently skips the Claude Code symlink when `.claude/` doesn't
 * already exist. Pre-creating `.claude/` lets the upstream CLI do the right
 * thing itself, so we don't have to know its layout.
 */

import { defineCommand } from 'citty'
import spawn from 'cross-spawn'
import { mkdirSync } from 'node:fs'
import { join } from 'node:path'
import { homedir } from 'node:os'

const SKILL_REPO = 'deepdotspace/deepspace-skill'
const SKILL_NAME = 'deepspace'

/**
 * Run `npx -y skills@latest add <repo> [extraArgs]`. Inherits stdio so the
 * `skills` CLI's own prompts / progress bars show through.
 */
function runSkillsAdd(extraArgs: string[]): Promise<number> {
  return new Promise((resolve) => {
    const child = spawn(
      'npx',
      ['-y', 'skills@latest', 'add', SKILL_REPO, ...extraArgs],
      { stdio: 'inherit' },
    )
    child.on('close', (code) => resolve(code ?? 1))
    child.on('error', (err) => {
      console.error(`Failed to invoke npx: ${err.message}`)
      console.error('Is Node.js installed and on PATH?')
      resolve(1)
    })
  })
}

/**
 * Workaround for https://github.com/vercel-labs/skills/issues/1138 — the
 * upstream installer's project-local branch silently returns success without
 * creating the Claude Code symlink when `<baseDir>/.claude/` doesn't exist.
 * Pre-creating an empty `.claude/` flips that check and lets the upstream CLI
 * create the symlink itself. Idempotent.
 *
 * @param baseDir  cwd for project-local install, $HOME for `-g` global.
 */
function precreateClaudeDir(baseDir: string): void {
  mkdirSync(join(baseDir, '.claude'), { recursive: true })
}

const add = defineCommand({
  meta: {
    name: 'add',
    description: 'Install the DeepSpace skill for your coding agent',
  },
  args: {
    global: {
      type: 'boolean',
      alias: 'g',
      description: 'Install globally (~/.agents/skills/) instead of into the current directory',
      default: false,
    },
    yes: {
      type: 'boolean',
      alias: 'y',
      description: 'Skip prompts (assume yes for any confirmation)',
      default: false,
    },
    agent: {
      type: 'string',
      description: 'Target a single agent (e.g. claude-code, codex, opencode). Default: install for all detected agents.',
      required: false,
    },
  },
  async run({ args }) {
    const extra: string[] = []
    if (args.global) extra.push('-g')
    if (args.yes) extra.push('-y')
    if (args.agent) extra.push('--agent', args.agent)

    // Pre-create .claude/ so upstream's install picks up Claude Code.
    // Skip when --agent targets a non-claude agent — no point creating
    // an empty .claude/ the user doesn't want.
    if (!args.agent || args.agent === 'claude-code') {
      const baseDir = args.global ? homedir() : process.cwd()
      try {
        precreateClaudeDir(baseDir)
      } catch (err: any) {
        // Non-fatal: upstream will still install for other agents.
        console.warn(`\n  Note: could not pre-create .claude/ for Claude Code: ${err?.message ?? err}`)
      }
    }

    const code = await runSkillsAdd(extra)
    if (code !== 0) process.exit(code)
  },
})

export default defineCommand({
  meta: {
    name: 'skills',
    description: 'Manage the DeepSpace agent skill (Claude Code, Codex, OpenCode, Cursor, ...)',
  },
  subCommands: {
    add,
  },
})
